import * as stylex from "@stylexjs/stylex";
import { Trash2 } from "lucide-react";
import { copy } from "@/copy";
import type { TagColor } from "@/domain/schemas";
import "@/theme/tokens.stylex";
import { colors, radii, shadows, space } from "@/theme/tokens.stylex";
import "@/theme/type.stylex";
import { type } from "@/theme/type.stylex";
import { Button } from "@/ui/Button";
import { Eyebrow } from "@/ui/Eyebrow";
import { IconButton } from "@/ui/IconButton";
import { tagFills } from "@/ui/tagFills";
export type Member = {
  id: string;
  name: string;
  color: TagColor;
  plans: number;
  result: string;
};
type MemberListProps = {
  members: readonly Member[];
  asking: string | null;
  onAsk: (id: string) => void;
  onCancel: () => void;
  onRemove: (id: string) => void;
};
type MemberRowProps = Omit<MemberListProps, "members" | "asking"> & {
  member: Member;
  asking: boolean;
  removable: boolean;
  divided: boolean;
};
const text = copy.settings.family;
const styles = {
  circle: {
    kzqmXN: "x100vrsf",
    kZKoxP: "x1vqgdyp",
    kmuXW: "x2lah0s",
    kaIpWk: "xqhrb7l",
    $$css: true
  },
  confirm: {
    ks0D6T: "x193iq5w",
    khDVqt: "xeaf4i8",
    k9WMMc: "x2b8uid",
    $$css: true
  }
};
export function MemberList({
  members,
  asking,
  onAsk,
  onCancel,
  onRemove
}: MemberListProps) {
  const removable = members.length > 1;
  return <section className="x78zum5 xdt5ytf x1hu4l6k xh540kj xwe7p7i xnfxhx2 xgkd4oa x9f619">
      <Eyebrow>{text.eyebrow(members.length)}</Eyebrow>
      <ul aria-label={text.list} className="x78zum5 xdt5ytf x1ghz6dp x674116 xe8uvvx x5r8ks9 x1fv5t7x">
        {members.map((member, index) => <MemberRow key={member.id} member={member} asking={asking === member.id} removable={removable} divided={index > 0} onAsk={onAsk} onCancel={onCancel} onRemove={onRemove} />)}
      </ul>
      {!removable && <span className="x14c3fy2 x1dld7ex x37zpob x1fzhlzt">{text.lastMember}</span>}
    </section>;
}
function MemberRow({
  member,
  asking,
  removable,
  divided,
  onAsk,
  onCancel,
  onRemove
}: MemberRowProps) {
  const label = text.removeName(member.name);
  return <li {...{
    0: {
      className: "x78zum5 x1a02dak x6s0dn4 x9snybx x8nclml x1b4vry6 x9f619"
    },
    1: {
      className: "x78zum5 x1a02dak x6s0dn4 x9snybx x8nclml x1b4vry6 x9f619 x3f47w4"
    }
  }[!!divided << 0]}>
      <div className="x78zum5 x6s0dn4 xddi9bj xeuugli x4r9l5d">
        <span aria-hidden {...stylex.props(styles.circle, tagFills[member.color])} />
        <div className="x78zum5 xdt5ytf x195vfkc xeuugli x98rzlu">
          <span className="x19d36u7 xfbjfs5 x1j7pf75 xb3r6kr xlyipyv xuxw1ft">{member.name}</span>
          {asking ? <span className="x14c3fy2 x1dld7ex x37zpob x1fzhlzt">{member.result}</span> : <Eyebrow size="caption">{text.plans(member.plans)}</Eyebrow>}
        </div>
      </div>
      {removable && !asking && <IconButton icon={Trash2} label={label} variant="canvas" size="touch" onClick={() => onAsk(member.id)} />}
      {asking && <div className="x78zum5 x1a02dak x6s0dn4 x13a6bvl x152posx x1gebrk9 xeuugli">
          <Button variant="ghost" onClick={onCancel}>
            {text.cancel}
          </Button>
          <Button variant="danger" icon={Trash2} onClick={() => onRemove(member.id)} xstyle={styles.confirm}>
            {label}
          </Button>
        </div>}
    </li>;
}